import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'expo-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useAskUiStore, useAskUiStoreApi } from '@/components/libraryChat/LibraryChatContext';
import { ROUTES } from '@/constants/routes';
import { useAskConversation } from '@/hooks/ask/useAskConversation';
import { useSubmitAskTurn } from '@/hooks/ask/useSubmitAskTurn';
import type { AskConversationSnapshot, AskTurn } from '@/services/ask/api';
import { toAskError, type AskError, type AskErrorKind } from '@/services/ask/errors';
import { forgetAskConversation } from '@/services/ask/queryOptions';
import {
  isActiveTurn,
  isRepeatResult,
  selectActiveTurn,
  selectLatestCompletedTurn,
} from '@/services/ask/selectors';
import type { AskNotice, AskUiStore } from '@/services/stores/askUiStore';
import {
  trackLibraryChatConversationOpened,
  trackLibraryChatTurnCompleted,
  trackLibraryChatTurnFailed,
} from '@/util/analyticsEvents';
import { Sentry } from '@/util/sentry';

/**
 * A send that did not go through, kept so the composer can say why and so a
 * resend of the same text reuses the same turn id.
 */
export type AskSendFailure = {
  kind: AskErrorKind;
  text: string;
  turnId: string;
  origin: 'composer' | 'retry';
};

type Attempt = {
  origin: AskSendFailure['origin'];
  turnId: string;
  text: string;
  parentTurnId: string | null;
  acknowledged: boolean;
  termsAccepted: boolean;
};

/**
 * Kinds whose turn id must not be reused for a resend. A stale parent needs a
 * new id by contract; the others end this conversation for the reader.
 */
const FRESH_ID_KINDS: ReadonlySet<AskErrorKind> = new Set([
  'CONVERSATION_ADVANCED',
  'CONVERSATION_LIMIT_REACHED',
  'NOT_FOUND',
]);

/** The backend answering as designed; everything else is worth a Sentry report. */
const EXPECTED_KINDS: ReadonlySet<AskErrorKind> = new Set([
  'NOT_FOUND',
  'CONVERSATION_ADVANCED',
  'TURN_IN_PROGRESS',
  'LIKELY_IDENTIFIER',
  'CONVERSATION_LIMIT_REACHED',
  'invalid',
  'npi_required',
  'forbidden',
  'network',
]);

/**
 * Reports each turn this device submitted once, when it first reaches a final
 * status, and only on this device: the socket brings the same turn to every
 * open device, and counting it on each would double the numbers.
 */
function useTurnOutcomeAnalytics(
  conversationId: string,
  snapshot: AskConversationSnapshot | undefined,
  submittedTurns: ReadonlyMap<string, number>,
  store: { getState: () => AskUiStore }
) {
  useEffect(() => {
    if (!snapshot || submittedTurns.size === 0) {
      return;
    }

    for (const turn of snapshot.turns) {
      const submittedAt = submittedTurns.get(turn.id);

      if (submittedAt == null || isActiveTurn(turn)) {
        continue;
      }

      store.getState().forgetSubmitted(turn.id);

      if (turn.status === 'completed') {
        trackLibraryChatTurnCompleted({
          conversation_id: conversationId,
          turn_id: turn.id,
          result_count: turn.results.length,
          repeat_count: turn.results.filter((result) => isRepeatResult(result, turn.id)).length,
          unavailable_count: turn.results.filter((result) => result.unavailable).length,
          context_changed: turn.context?.contextChanged ?? false,
          duration_ms: Date.now() - submittedAt,
        });
      } else {
        trackLibraryChatTurnFailed({
          conversation_id: conversationId,
          turn_id: turn.id,
          error_code: turn.status === 'stalled' ? 'stalled' : (turn.errorCode ?? 'failed'),
        });
      }
    }
  }, [conversationId, snapshot, submittedTurns, store]);
}

/**
 * Everything one conversation screen does, apart from drawing it.
 *
 * Sending never waits on a search: the server accepts the turn and the
 * snapshot, refreshed by hints, polling or focus, carries it to its end.
 *
 * A send takes a fresh turn id unless it repeats the text of the last failed
 * send, in which case it reuses that id. That is what makes "try again" after
 * a dropped request, or "send anyway" after the identifier warning, land as
 * the same turn rather than a second one.
 *
 * A new topic's first question landing also folds the desktop history column
 * for the visit, so its results get the room. It waits for the server to accept
 * the turn, so a refused question leaves the column where it was, and it
 * follows any accepted first send, including a retry or "send anyway".
 *
 * A conversation that disappears, whether it was never the reader's, was
 * deleted, or was deleted on another device, sends the reader to a new
 * conversation with a notice saying so, and is forgotten from every cache.
 */
export function useAskConversationController(conversationId: string, isNew: boolean) {
  const router = useRouter();

  const queryClient = useQueryClient();

  const store = useAskUiStoreApi();

  const query = useAskConversation(conversationId, { isNew });

  const { data: snapshot, refetch, isPending: isSnapshotPending, error: snapshotError } = query;

  const { mutateAsync, isPending: isSending } = useSubmitAskTurn();

  const [failure, setFailure] = useState<AskSendFailure | null>(null);

  const deletedElsewhere = useAskUiStore((state) => state.deletedElsewhere);

  const submittedTurns = useAskUiStore((state) => state.submittedTurns);

  const activeTurn = selectActiveTurn(snapshot);

  const latestCompletedTurn = selectLatestCompletedTurn(snapshot);

  const leave = useCallback(
    (notice: AskNotice) => {
      forgetAskConversation(queryClient, conversationId);

      store.getState().setNotice(notice);

      router.replace(ROUTES.LIBRARY_CHAT);
    },
    [conversationId, queryClient, router, store]
  );

  const run = useCallback(
    async (attempt: Attempt): Promise<AskError | null> => {
      setFailure(null);

      try {
        await mutateAsync({
          conversationId,
          turnId: attempt.turnId,
          text: attempt.text,
          parentTurnId: attempt.parentTurnId,
          acknowledgedIdentifierWarning: attempt.acknowledged,
          isRetry: attempt.origin === 'retry',
          termsAccepted: attempt.termsAccepted,
        });

        if (isNew) {
          store.getState().setNextEntry('continued');

          store.getState().foldHistoryForVisit();

          router.replace(ROUTES.LIBRARY_CHAT_CONVERSATION(conversationId));
        }

        return null;
      } catch (caught) {
        const error = toAskError(caught);

        setFailure({
          kind: error.kind,
          text: attempt.text,
          turnId: attempt.turnId,
          origin: attempt.origin,
        });

        if (error.kind === 'NOT_FOUND') {
          leave('not_found');
        }

        if (error.kind === 'TURN_IN_PROGRESS') {
          refetch().catch(() => {});
        }

        if (!EXPECTED_KINDS.has(error.kind)) {
          Sentry.captureException(error, {
            tags: { component: 'LibraryChat', source: 'ask-submit-turn' },
            extra: {
              errorMessage: error.message,
              status: error.status,
              conversationId,
              turnId: attempt.turnId,
            },
          });
        }

        return error;
      }
    },
    [conversationId, isNew, leave, mutateAsync, refetch, router, store]
  );

  /**
   * Sends the reader's question. Identifier screening is the server's alone, so
   * a composer send never carries the identifier acknowledgement.
   */
  const send = useCallback(
    (text: string, { termsAccepted = false }: { termsAccepted?: boolean } = {}) => {
      const canReuse =
        failure?.origin === 'composer' &&
        failure.text === text &&
        !FRESH_ID_KINDS.has(failure.kind);

      return run({
        origin: 'composer',
        turnId: canReuse ? failure.turnId : uuidv4(),
        text,
        parentTurnId: latestCompletedTurn?.id ?? null,
        acknowledged: false,
        termsAccepted,
      });
    },
    [failure, latestCompletedTurn, run]
  );

  /**
   * Asks a failed or stalled turn again: a new turn id, the same parent. The
   * text was already accepted once, so its identifier warning — and, for a
   * conversation's first turn, its terms acceptance — was answered when it
   * was first sent and is not asked again.
   */
  const retryTurn = useCallback(
    (turn: AskTurn) =>
      run({
        origin: 'retry',
        turnId: uuidv4(),
        text: turn.text,
        parentTurnId: turn.parentTurnId,
        acknowledged: true,
        termsAccepted: true,
      }),
    [run]
  );

  const startNewWithDraft = useCallback(
    (draft: string) => {
      store.getState().setHandoffDraft(draft);

      store.getState().setNotice('conversation_full');

      router.replace(ROUTES.LIBRARY_CHAT);
    },
    [router, store]
  );

  /** Stable, because a focus effect depends on it and would otherwise refetch every render. */
  const reload = useCallback(() => {
    refetch().catch(() => {});
  }, [refetch]);

  useEffect(() => {
    if (deletedElsewhere === conversationId) {
      store.getState().markDeletedElsewhere(null);

      leave('deleted_elsewhere');
    }
  }, [conversationId, deletedElsewhere, leave, store]);

  const snapshotErrorKind = (snapshotError as AskError | null)?.kind;

  useEffect(() => {
    if (snapshotErrorKind === 'NOT_FOUND') {
      leave('not_found');
    }
  }, [leave, snapshotErrorKind]);

  const hasReportedOpen = useRef(false);

  const hasSnapshot = snapshot != null;

  useEffect(() => {
    if (hasReportedOpen.current || (!isNew && !hasSnapshot)) {
      return;
    }

    hasReportedOpen.current = true;

    const entry = isNew ? 'new' : store.getState().takeNextEntry();

    if (entry === 'continued') {
      return;
    }

    trackLibraryChatConversationOpened({
      conversation_id: conversationId,
      turn_count: snapshot?.turns.length ?? 0,
      entry: entry ?? 'link',
    });
  }, [conversationId, hasSnapshot, isNew, snapshot, store]);

  useTurnOutcomeAnalytics(conversationId, snapshot, submittedTurns, store);

  return useMemo(
    () => ({
      conversationId,
      isNew,
      snapshot,
      isLoading: !isNew && isSnapshotPending,
      loadError: snapshotErrorKind && snapshotErrorKind !== 'NOT_FOUND' ? snapshotErrorKind : null,
      reload,
      activeTurn,
      latestCompletedTurn,
      isSending,
      failure,
      send,
      retryTurn,
      startNewWithDraft,
    }),
    [
      activeTurn,
      conversationId,
      failure,
      isNew,
      isSending,
      isSnapshotPending,
      latestCompletedTurn,
      reload,
      retryTurn,
      send,
      snapshot,
      snapshotErrorKind,
      startNewWithDraft,
    ]
  );
}

export type AskConversationController = ReturnType<typeof useAskConversationController>;
